/* EoxBulletinViewRenderer.js */

"use strict";
const _ = require("lodash");
const EntityGrid = require("@gluenetworks/entity_grid_gl").Grid;
const Forms = require("@gluenetworks/forms");
const FileName = require("@gluenetworks/steve_utilities").fileName;
const FormsComponent = Forms.ui.component;
require("moment-duration-format");

const moment = require("moment");

const DeviceProdIdCustomField = "43236483-c177-42f1-aed5-b972ebe23eb8";

class EoxBulletinViewRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("eox-bulletin-view-gl");
        this._rootElem.component = this._model;
        this._logger = this._model.logger;

        this._formsComponent = new FormsComponent();
        this._fileNamer = FileName;

        // TODO will create separate hyperlink Control module
        this._bulletinButton = document.createElement("a");
        this._bulletinButton.setAttribute("class", "hyperlink");
        this._bulletinButton.setAttribute("target", "_blank");

        this._productNameButton = document.createElement("a");
        this._productNameButton.setAttribute("class", "hyperlink");
        this._productNameButton.setAttribute("target", "_blank");

        this._defaultViewActive = undefined;
        this._compEoxGrid = undefined;
        this._phoneModelEoxGrid = undefined;
        this._gridRows = undefined;

        this._compEoxCount = undefined;
        this._phoneModelEoxCount = undefined;

        this._renderingForm = false;
        this._viewActionsAdded = false;
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _registerListeners() {
        this._model.on(
            "PristinePropertiesChanged",
            (this._onPristinePropertiesChanged = this._onPristinePropertiesChanged.bind(this))
        );

        this._model._compPagingFeed.on(
            "RefreshView",
            (this._onFeedRefreshView = this._onFeedRefreshView.bind(this))
        );
        if (this._model._phoneModelPagingFeed) {
            this._model._phoneModelPagingFeed.on(
                "RefreshView",
                (this._onPhoneModelFeedRefreshView = this._onPhoneModelFeedRefreshView.bind(this))
            );
        }
        this._registerGridListeners();
    }

    _registerGridListeners() {
        if (this._compEoxGrid) {
            this._compEoxGrid.on(
                "UserSorted",
                (this._onUserSorted = this._onUserSorted.bind(this))
            );
        }
        if (this._phoneModelEoxGrid) {
            this._phoneModelEoxGrid.on(
                "UserSorted",
                (this._onPhoneModelUserSorted = this._onPhoneModelUserSorted.bind(this))
            );
        }
    }

    _unregisterListeners() {
        this._model.removeListener("PristinePropertiesChanged", this._onPristinePropertiesChanged);
    }

    _unregisterGridListeners() {
        if (this._compEoxGrid) {
            this._compEoxGrid.removeListener("UserSorted", this._onUserSorted);
        }
        if (this._phoneModelEoxGrid) {
            this._phoneModelEoxGrid.removeListener("UserSorted", this._onPhoneModelUserSorted);
        }
    }

    _showDefaultMessageDisplay(type) {
        let message = "No component EOX to display";
        if (type === "phone") {
            message = "No phone model EOX to display";
        }
        return this._model.gameEditor
            .showDefaultMessageView({
                message,
            })
            .then(() => {
                this._defaultViewActive = true;
            })
            .catch((err) => {
                this._logger.warn(
                    {error: err},
                    "EoxBulletinViewRenderer._showDefaultMessageDisplay error"
                );
            });
    }

    _hideDefaultMessageDisplay(type) {
        let message = "No component EOX to display";
        if (type === "phone") {
            message = "No phone model EOX to display";
        }
        return this._model.gameEditor
            .hideDefaultMessageView({
                message,
            })
            .then(() => {
                this._defaultViewActive = false;
            })
            .catch((err) => {
                this._logger.warn(
                    {error: err},
                    "EoxBulletinViewRenderer._hideDefaultMessageDisplay error"
                );
            });
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    shutdown() {
        this._unregisterListeners();
        this._unregisterGridListeners();
        this._removeViewActions();
        let promArr = [this._formsComponent.shutdown];
        return Promise.all(promArr).then(
            () => {
                if (!_.isUndefined(this._compEoxGrid)) {
                    this._compEoxGrid.shutdown();
                }
                if (!_.isUndefined(this._phoneModelEoxGrid)) {
                    this._phoneModelEoxGrid.shutdown();
                }

                this._model = undefined;
                this._formsComponent = undefined;
                this._renderingForm = undefined;
                this._viewActionsAdded = undefined;
                this._bulletinButton = undefined;
                this._productNameButton = undefined;
                this._rootElem.remove();
                this._rootElem = undefined;
                this._logger = undefined;
                this._defaultViewActive = undefined;
                this._compEoxGrid = undefined;
                this._phoneModelEoxGrid = undefined;
                this._compEoxCount = undefined;
                this._phoneModelEoxCount = undefined;
                this._gridRows = undefined;
            },
            (err) => {
                this._logger.warn({error: err}, "EoxBulletinViewRenderer.shutdown() error.");
            }
        );
    }

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        if (!this._rootElem.hasChildNodes()) {
            this._rootElem.appendChild(this._formsComponent.renderer.rootElement);
        }
        return this._model.whenModelIsReady().then(
            () => {
                this._compEoxGrid = new EntityGrid({
                    gridKey: this._model._gridKey,
                    columns: this._model.columnConfig,
                    pagingFeed: this._model._compPagingFeed,
                    showHeader: true,
                });

                //We need to find if the cache is empty or not
                this._compEoxCount = Object.keys(this._model._compPagingFeed._feed._cache).length;

                // Initialize phone model EoX grid
                if (this._model._phoneModelPagingFeed) {
                    this._phoneModelEoxGrid = new EntityGrid({
                        gridKey: this._model._gridKey + "_phoneModel",
                        columns: this._model.phoneModelColumnConfig,
                        pagingFeed: this._model._phoneModelPagingFeed,
                        showHeader: true,
                    });
                    this._phoneModelEoxCount = Object.keys(
                        this._model._phoneModelPagingFeed._feed._cache
                    ).length;
                }

                this._registerListeners();
                return this._renderDeviceDetails();
            },
            (err) => err
        );
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START PRIVATE ACTION METHODS            */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE ACTION METHODS              */
    /* START PRIVATE RENDER METHODS            */
    /*******************************************/

    _renderDeviceDetails() {
        return Promise.resolve().then(
            () => {
                let jtype = this._getJtype();
                let pristineValue = this._getPristineValue();
                this._formsComponent.setTypeInformation(jtype, true);
                this._formsComponent.setPristineValue(pristineValue, true);

                this._renderingForm = true;
                return this._formsComponent.renderer
                    .render()
                    .then(() => this._renderLinkButtons())
                    .then(() => {
                        if (this._model._deviceEox) {
                            this._addViewActions();
                        }
                    })
                    .then(() => {
                        if (this._compEoxCount) {
                            this._rootElem
                                .getElementsByClassName("comp_eox_block")[0]
                                .getElementsByClassName("nested_rendered_controls")[0]
                                .appendChild(this._compEoxGrid.renderer.rootElement);
                        }
                    })
                    .then(() => {
                        if (this._phoneModelEoxCount) {
                            this._rootElem
                                .getElementsByClassName("phone_model_eox_block")[0]
                                .getElementsByClassName("nested_rendered_controls")[0]
                                .appendChild(this._phoneModelEoxGrid.renderer.rootElement);
                        }
                    })
                    .then(() => {
                        this._renderingForm = false;
                    });
            },
            (err) => {
                this._logger.warn(
                    {error: err},
                    "EoxBulletinViewRenderer._renderEoxBulletinDetails() error."
                );
            }
        );
    }

    // TODO Temporary: To be moved out to a different module for hyperlink
    _renderLinkButtons() {
        let pristineEoxProperties = this._model._deviceEox
            ? this._model._deviceEox.getPristineProperties()
            : null;

        let bulletinInput = _.get(this._rootElem.querySelectorAll(".bulletin_number"), 0, false);

        if (bulletinInput) {
            let bulletinButtonContainer = _.get(
                bulletinInput.querySelectorAll("input-text-control"),
                0,
                false
            );
            // TODO To be refactored with feed value
            if (pristineEoxProperties && pristineEoxProperties.data.ProductBulletinNumber) {
                this._bulletinButton.innerHTML = pristineEoxProperties.data.ProductBulletinNumber;
                this._bulletinButton.setAttribute(
                    "href",
                    pristineEoxProperties.data.LinkToProductBulletinURL
                );
            } else {
                this._bulletinButton.innerHTML = "Not Available";
                this._bulletinButton.setAttribute("href", "#");
                this._bulletinButton.setAttribute("class", "hyperlink disabled");
            }
            if (bulletinButtonContainer) {
                bulletinButtonContainer.appendChild(this._bulletinButton);

                // Remove standard input
                bulletinButtonContainer.querySelectorAll("input")[0].remove();
                //bulletinInput = undefined;
            }
        }
        let productNameInput = document.getElementById("ProductName");
        if (productNameInput) {
            // REASON: productName input won't exist if migration data doesn't exist
            let productNameContainer = productNameInput.parentElement;
            // To be refactored with feed value
            if (
                pristineEoxProperties &&
                pristineEoxProperties.data.EOXMigrationDetails.MigrationProductId
            ) {
                this._productNameButton.innerHTML =
                    pristineEoxProperties.data.EOXMigrationDetails.MigrationProductId;
                this._productNameButton.setAttribute(
                    "href",
                    pristineEoxProperties.data.EOXMigrationDetails.MigrationProductInfoURL
                );
            } else {
                this._productNameButton.innerHTML = "Not Available";
                this._productNameButton.setAttribute("href", "#");
                this._productNameButton.setAttribute("class", "hyperlink disabled");
            }
            productNameContainer.appendChild(this._productNameButton);

            // Remove standard input
            productNameInput.remove();
            productNameInput = undefined;
        }

        return Promise.resolve();
    }

    _addViewActions() {
        if (!this._viewActionsAdded) {
            this.model.gameEditor.addViewAction({
                type: "normal",
                displayName: "Download",
                name: "DownloadEox",
                handler: (this._onDownloadEox = this._onDownloadEox.bind(this)),
            });
            this._viewActionsAdded = true;
        }
    }

    _removeViewActions() {
        if (this._viewActionsAdded) {
            this.model.gameEditor.clearViewActions();
        }
    }

    async _onDownloadEox() {
        this.model.gameEditor.disableViewAction("DownloadEox");
        const deviceDetials = this.model._device,
            eoxData = this._model._deviceEox
                ? this._model._deviceEox.getPristineProperties()
                : null,
            componentEox = await this._model.loadComponentEox(),
            phoneModelEox = this._model._phoneModelPagingFeed
                ? await this._model.loadPhoneModelEox()
                : [],
            eoxAnnouncementDate = eoxData
                ? eoxData.data.EOXExternalAnnouncementDate.value
                : new Date(),
            fileName = this._fileNamer({
                solution: "DM",
                general: "EoXBulletin",
                specific: deviceDetials.pristineProperties.name,
                date: new Date(eoxAnnouncementDate),
                suffix: "csv",
            });

        /*
                this._model._componentEox.forEach( (val) => {
            pristineValue.eoxComponentBlock.push(val.getPristineProperties());
        });
         */

        let printdata = {};
        printdata.Name = deviceDetials.name;
        printdata.Description = deviceDetials.description;
        if (eoxData) {
            printdata.ProductId = eoxData.data.EOLProductID;
            printdata.ProductDescription = eoxData.data.ProductIDDescription;
            printdata.BulletinNumber = eoxData.data.ProductBulletinNumber;
            printdata.AnnouncementDate = eoxData.data.EOXExternalAnnouncementDate.value;
            printdata.EndOfSale = eoxData.data.EndOfSaleDate.value;
            printdata.EndSoftwareMaintenance = eoxData.data.EndOfSWMaintenanceReleases.value;
            printdata.EndSecuritySupport = eoxData.data.EndOfSecurityVulSupportDate.value;
            printdata.EndRoutineFailureAnalysis =
                eoxData.data.EndOfRoutineFailureAnalysisDate.value;
            printdata.EndContractRenewal = eoxData.data.EndOfServiceContractRenewal.value;
            printdata.LastDateSupport = eoxData.data.LastDateOfSupport.value;
            printdata.EndServiceAttachment = eoxData.data.EndOfSvcAttachDate.value;
            if (eoxData.data.EOXMigrationDetails.PIDActiveFlag === "Y") {
                printdata.MigrationInformation =
                    eoxData.data.EOXMigrationDetails.MigrationInformation;
                printdata.MigrationOption = eoxData.data.EOXMigrationDetails.MigrationOption;
                printdata.MigrationproductName =
                    eoxData.data.EOXMigrationDetails.MigrationProductName;
                printdata.MigrationStrategy = eoxData.data.EOXMigrationDetails.MigrationStrategy;
                printdata.MigrationproductName =
                    eoxData.data.EOXMigrationDetails.MigrationProductName;
                printdata.MigrationproductId = eoxData.data.EOXMigrationDetails.MigrationProductId;
                printdata.MigrationproductInfoURL =
                    eoxData.data.EOXMigrationDetails.MigrationProductInfoURL;
                printdata.MigrationStrategy = eoxData.data.EOXMigrationDetails.MigrationStrategy;
            }

            if (componentEox.length > 0) {
                componentEox.forEach((val, idx) => {
                    let data = val.getPristineProperties().data;
                    let name = val.getPristineProperties().data.EOLProductID || idx;
                    if (name) {
                        printdata["Component_" + name + "_EOLProductID"] = data.EOLProductID;
                        printdata["Component_" + name + "_ProductIDDescription"] =
                            data.ProductIDDescription;
                        printdata["Component_" + name + "_ProductBulletinNumber"] =
                            data.ProductBulletinNumber;
                        printdata["Component_" + name + "_EOXExternalAnnouncementDate"] =
                            data.EOXExternalAnnouncementDate.value;
                        printdata["Component_" + name + "_EndOfSaleDate"] =
                            data.EndOfSaleDate.value;
                        printdata["Component_" + name + "_EndOfSWMaintenanceReleases"] =
                            data.EndOfSWMaintenanceReleases.value;
                        printdata["Component_" + name + "_EOXExternalAnnouncementDate"] =
                            data.EOXExternalAnnouncementDate.value;
                        printdata["Component_" + name + "_EndOfSaleDate"] =
                            data.EndOfSaleDate.value;
                        printdata["Component_" + name + "_EndOfSWMaintenanceReleases"] =
                            data.EndOfSWMaintenanceReleases.value;
                        printdata["Component_" + name + "_EOXExternalAnnouncementDate"] =
                            data.EOXExternalAnnouncementDate.value;
                        printdata["Component_" + name + "_EOXExternalAnnouncementDate"] =
                            data.EOXExternalAnnouncementDate.value;
                        printdata["Component_" + name + "_EndOfServiceContractRenewal"] =
                            data.EndOfServiceContractRenewal.value;
                        printdata["Component_" + name + "_LastDateOfSupport"] =
                            data.LastDateOfSupport.value;
                        printdata["Component_" + name + "_EndOfSvcAttachDate"] =
                            data.EndOfSvcAttachDate.value;
                    }
                });
            }

            if (phoneModelEox.length > 0) {
                const detectedPhoneModels = deviceDetials.detectedPhoneModels || {};
                phoneModelEox.forEach((val, idx) => {
                    let data = val.getPristineProperties().data;
                    let pid = data.EOLProductID || idx;
                    // Find the model name from detectedPhoneModels
                    let modelName = pid;
                    for (const [model, modelData] of Object.entries(detectedPhoneModels)) {
                        if (modelData.pid === pid) {
                            modelName = model;
                            break;
                        }
                    }
                    if (pid) {
                        printdata["PhoneModel_" + modelName + "_Model"] = modelName;
                        printdata["PhoneModel_" + modelName + "_EOLProductID"] = data.EOLProductID;
                        printdata["PhoneModel_" + modelName + "_ProductIDDescription"] =
                            data.ProductIDDescription;
                        printdata["PhoneModel_" + modelName + "_ProductBulletinNumber"] =
                            data.ProductBulletinNumber;
                        printdata["PhoneModel_" + modelName + "_EndOfSaleDate"] =
                            data.EndOfSaleDate.value;
                        printdata["PhoneModel_" + modelName + "_EndOfSWMaintenanceReleases"] =
                            data.EndOfSWMaintenanceReleases.value;
                        printdata["PhoneModel_" + modelName + "_EndOfRoutineFailureAnalysisDate"] =
                            data.EndOfRoutineFailureAnalysisDate.value;
                        printdata["PhoneModel_" + modelName + "_EndOfSecurityVulSupportDate"] =
                            data.EndOfSecurityVulSupportDate.value;
                        printdata["PhoneModel_" + modelName + "_LastDateOfSupport"] =
                            data.LastDateOfSupport.value;
                        printdata["PhoneModel_" + modelName + "_EndOfSvcAttachDate"] =
                            data.EndOfSvcAttachDate.value;
                    }
                });
            }

            this.model.gameEditor.saveCSVFile(
                fileName,
                this.model.gameEditor.convertToCSV(printdata)
            );
        }

        this.model.gameEditor.enableViewAction("DownloadEox");

        return this;
    }

    /*******************************************/
    /* END PRIVATE RENDER METHODS              */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onPristinePropertiesChanged() {
        if (!this._renderingForm) {
            this._formsComponent.setPristineValue(this._getPristineValue());
            // TODO Workaround as _formsComponent doesn't provide promises or events to wait for rendering
            _.delay(_.bind(this._renderLinkButtons, this), 500);
        }
    }

    _onUserSorted(eventData) {
        this._model._onUserSorted(eventData);
    }

    _onPhoneModelUserSorted(eventData) {
        eventData = [].concat(eventData);
        let sort = {};

        eventData.forEach((sortData) => {
            if (
                !sortData ||
                !sortData.sortColumn ||
                !this._model.phoneModelColumnConfig[sortData.sortColumn]
            ) {
                return;
            }
            const sortCriteria =
                this._model.phoneModelColumnConfig[sortData.sortColumn].valuePath ||
                this._model.phoneModelColumnConfig[sortData.sortColumn].path ||
                sortData.sortColumn;
            sort[sortCriteria] = sortData.sort === "asc" ? 1 : -1;
        });

        sort = this._model._patchSorting(sort);
        this._model._phoneModelPagingFeed.setSort(sort);
    }

    _onFeedRefreshView() {
        let totalEntities = this._model._compPagingFeed.totalEntities;
        if (totalEntities === 0) {
            if (!this._defaultViewActive) {
                this._showDefaultMessageDisplay();
            }
        } else {
            if (this._defaultViewActive) {
                this._hideDefaultMessageDisplay();
            }
        }
    }

    _onPhoneModelFeedRefreshView() {
        let totalEntities = this._model._phoneModelPagingFeed.totalEntities;
        if (totalEntities === 0) {
            if (!this._defaultViewActive) {
                this._showDefaultMessageDisplay("phone");
            }
        } else {
            if (this._defaultViewActive) {
                this._hideDefaultMessageDisplay("phone");
            }
        }
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/

    _toLocaleString(val) {
        const d = new Date(moment(val, "YYYY-MM-DD").toLocaleString());
        if (Object.prototype.toString.call(d) === "[object Date]" && !isNaN(d.getTime())) {
            return d.toLocaleDateString();
        } else {
            return "Not Available";
        }
    }

    _getPristineValue() {
        let pristineProperties = this._model._device.getPristineProperties();
        let pristineEoxProperties = this._model._deviceEox
            ? this._model._deviceEox.getPristineProperties()
            : null;
        let pristineValue = {};
        const deviceProdId = _.get(
            this._model._device.customFieldValues,
            DeviceProdIdCustomField,
            undefined
        );

        if (pristineProperties) {
            pristineValue.nameEox = pristineProperties.name;
            pristineValue.description = pristineProperties.description;
            pristineValue.productId = deviceProdId; //set a default product Id if available

            if (pristineEoxProperties && pristineEoxProperties.data) {
                pristineValue.productId = pristineEoxProperties.data.EOLProductID;
                pristineValue.productDescription = pristineEoxProperties.data.ProductIDDescription;
                pristineValue.announcementDate = this._toLocaleString(
                    pristineEoxProperties.data.EOXExternalAnnouncementDate.value
                );
                pristineValue.eoxBlock = {};
                pristineValue.eoxBlock.endOfSale = this._toLocaleString(
                    pristineEoxProperties.data.EndOfSaleDate.value
                );
                pristineValue.eoxBlock.endSoftwareMaintenance = this._toLocaleString(
                    pristineEoxProperties.data.EndOfSWMaintenanceReleases.value
                );
                pristineValue.eoxBlock.endSecuritySupport = this._toLocaleString(
                    pristineEoxProperties.data.EndOfSecurityVulSupportDate.value
                );
                pristineValue.eoxBlock.endRoutineFailureAnalysis = this._toLocaleString(
                    pristineEoxProperties.data.EndOfRoutineFailureAnalysisDate.value
                );
                pristineValue.eoxBlock.endContractRenewal = this._toLocaleString(
                    pristineEoxProperties.data.EndOfServiceContractRenewal.value
                );
                pristineValue.eoxBlock.lastDateSupport = this._toLocaleString(
                    pristineEoxProperties.data.LastDateOfSupport.value
                );
                pristineValue.eoxBlock.endServiceAttachment = this._toLocaleString(
                    pristineEoxProperties.data.EndOfSvcAttachDate.value
                );

                if (
                    pristineEoxProperties &&
                    pristineEoxProperties.data.EOXMigrationDetails.PIDActiveFlag === "Y"
                ) {
                    pristineValue.migrationBlock = {};
                    pristineValue.migrationBlock.migrationInformation =
                        pristineEoxProperties.data.EOXMigrationDetails.MigrationInformation;
                    pristineValue.migrationBlock.migrationOption =
                        pristineEoxProperties.data.EOXMigrationDetails.MigrationOption;
                    pristineValue.migrationBlock.productName =
                        pristineEoxProperties.data.EOXMigrationDetails.MigrationProductName;
                    pristineValue.migrationBlock.migrationStrategy =
                        pristineEoxProperties.data.EOXMigrationDetails.MigrationStrategy;
                    pristineValue.migrationBlock.migrationProductId =
                        pristineEoxProperties.data.EOXMigrationDetails.MigrationProductId;
                    pristineValue.migrationBlock.migrationProductInfoURL =
                        pristineEoxProperties.data.EOXMigrationDetails.MigrationProductInfoURL;
                }
            }
        }
        return pristineValue;
    }

    _getJtype() {
        let pristineEoxProperties = this._model._deviceEox
            ? this._model._deviceEox.getPristineProperties()
            : null;
        let jtype = {};

        jtype.nameEox = "";
        jtype["nameEox@Display.label"] = "Name";
        jtype["nameEox@Display.readOnly"] = true;

        jtype.description = "";
        jtype["description@Display.label"] = "Description";
        jtype["description@Display.description"] = "Device Description";
        jtype["description@Display.placeholder"] = "Not Available";
        jtype["description@Display.readOnly"] = true;

        jtype.productId = "";
        jtype["productId@Display.label"] = "Product ID";
        jtype["productId@Display.description"] = "Cisco Product Identiﬁer";
        jtype["productId@Display.placeholder"] = "Not Available";
        jtype["productId@Display.readOnly"] = true;

        jtype.productDescription = "";
        jtype["productDescription@Display.label"] = "Product Description";
        jtype["productDescription@Display.description"] = "Cisco Product ID Description";
        jtype["productDescription@Display.placeholder"] = "Not Available";
        jtype["productDescription@Display.readOnly"] = true;

        jtype.bulletinNumber = "";
        jtype["bulletinNumber@Display.label"] = "Bulletin Number";
        jtype["bulletinNumber@Display.description"] = "Cisco Product Bulletin Number";
        jtype["bulletinNumber@Display.placeholder"] = "Not Available";
        jtype["bulletinNumber@Display.readOnly"] = true;

        jtype.announcementDate = "";
        jtype["announcementDate@Display.label"] = "Announcement Date";
        jtype["announcementDate@Display.description"] = "Cisco EOX External Announcement  Date";
        jtype["announcementDate@Display.placeholder"] = "Not Available";
        jtype["announcementDate@Display.readOnly"] = true;

        jtype.lineBreak1 = null;
        jtype["lineBreak1@Display.label"] = "";

        jtype.eoxBlock = {};
        jtype["eoxBlock@Display.label"] = "EoX";

        jtype.eoxBlock.endOfSale = "";
        jtype.eoxBlock["endOfSale@Display.label"] = "End of Sale (EoS)";
        jtype.eoxBlock["endOfSale@Display.description"] = "Cisco Last Day of Sales";
        jtype.eoxBlock["endOfSale@Display.placeholder"] = "Not Available";
        jtype.eoxBlock["endOfSale@Display.readOnly"] = true;

        jtype.eoxBlock.endSoftwareMaintenance = "";
        jtype.eoxBlock["endSoftwareMaintenance@Display.label"] =
            "End of Software Maintenance Release (EoSMR)";
        jtype.eoxBlock["endSoftwareMaintenance@Display.description"] =
            "Cisco Last Day of Software Maintenance Releases ";
        jtype.eoxBlock["endSoftwareMaintenance@Display.placeholder"] = "Not Available";
        jtype.eoxBlock["endSoftwareMaintenance@Display.readOnly"] = true;

        jtype.eoxBlock.endSecuritySupport = "";
        jtype.eoxBlock["endSecuritySupport@Display.label"] = "End of Security Support (EoSS)";
        jtype.eoxBlock["endSecuritySupport@Display.description"] =
            "Cisco Last Day of Security Vulnerability Support";
        jtype.eoxBlock["endSecuritySupport@Display.placeholder"] = "Not Available";
        jtype.eoxBlock["endSecuritySupport@Display.readOnly"] = true;

        jtype.eoxBlock.endRoutineFailureAnalysis = "";
        jtype.eoxBlock["endRoutineFailureAnalysis@Display.label"] =
            "End of Routine Failure Analysis (EoRFA)";
        jtype.eoxBlock["endRoutineFailureAnalysis@Display.description"] =
            "Cisco Last Day of Routine Failure Analysis";
        jtype.eoxBlock["endRoutineFailureAnalysis@Display.placeholder"] = "Not Available";
        jtype.eoxBlock["endRoutineFailureAnalysis@Display.readOnly"] = true;

        jtype.eoxBlock.endContractRenewal = "";
        jtype.eoxBlock["endContractRenewal@Display.label"] = "End of Contract Renewal (EoCR)";
        jtype.eoxBlock["endContractRenewal@Display.description"] =
            "Cisco Last Day of Service Contract Renewal";
        jtype.eoxBlock["endContractRenewal@Display.placeholder"] = "Not Available";
        jtype.eoxBlock["endContractRenewal@Display.readOnly"] = true;

        jtype.eoxBlock.lastDateSupport = "";
        jtype.eoxBlock["lastDateSupport@Display.label"] = "Last Date of Support (LDS)";
        jtype.eoxBlock["lastDateSupport@Display.description"] =
            "Cisco Last Day of Product Support (LDS)";
        jtype.eoxBlock["lastDateSupport@Display.placeholder"] = "Not Available";
        jtype.eoxBlock["lastDateSupport@Display.readOnly"] = true;

        jtype.eoxBlock.endServiceAttachment = "";
        jtype.eoxBlock["endServiceAttachment@Display.label"] = "End of Service Attachment (EoSA)";
        jtype.eoxBlock["endServiceAttachment@Display.description"] =
            "Cisco Last Day to order a new contract or add equipment or software to an existing contract";
        jtype.eoxBlock["endServiceAttachment@Display.placeholder"] = "Not Available";
        jtype.eoxBlock["endServiceAttachment@Display.readOnly"] = true;

        jtype.lineBreak2 = null;
        jtype["lineBreak2@Display.label"] = "";

        if (this._compEoxCount) {
            jtype.compEoxBlock = {};
            jtype["compEoxBlock@Display.label"] = "Component EoX";

            jtype.lineBreak3 = null;
            jtype["lineBreak3@Display.label"] = "";
        }

        if (this._phoneModelEoxCount) {
            jtype.phoneModelEoxBlock = {};
            jtype["phoneModelEoxBlock@Display.label"] = "Phone Model EoX";

            jtype.lineBreak4 = null;
            jtype["lineBreak4@Display.label"] = "";
        }

        jtype.migrationBlock = {};
        jtype["migrationBlock@Display.label"] = "Migration Details";

        if (
            pristineEoxProperties &&
            pristineEoxProperties.data.EOXMigrationDetails.PIDActiveFlag === "Y"
        ) {
            jtype.migrationBlock.migrationInformation = "";
            jtype.migrationBlock["migrationInformation@Display.label"] = "Migration Information";
            jtype.migrationBlock["migrationInformation@Display.description"] =
                "Cisco Migration Information";
            jtype.migrationBlock["migrationInformation@Display.placeholder"] = "Not Available";
            jtype.migrationBlock["migrationInformation@Display.readOnly"] = true;

            jtype.migrationBlock.migrationOption = "";
            jtype.migrationBlock["migrationOption@Display.label"] = "Migration Option";
            jtype.migrationBlock["migrationOption@Display.description"] = "Cisco Migration Option";
            jtype.migrationBlock["migrationOption@Display.placeholder"] = "Not Available";
            jtype.migrationBlock["migrationOption@Display.readOnly"] = true;

            jtype.migrationBlock.productName = "";
            jtype.migrationBlock["productName@Display.label"] = "Product Name";
            jtype.migrationBlock["productName@Display.description"] = "Cisco Replacement Product";
            jtype.migrationBlock["productName@Display.placeholder"] = "Not Available";
            jtype.migrationBlock["productName@Display.readOnly"] = true;

            jtype.migrationBlock.migrationProductId = "";
            jtype.migrationBlock["migrationProductId@Display.label"] = "Migration ID";
            jtype.migrationBlock["migrationProductId@Display.description"] = "Cisco Product ID";
            jtype.migrationBlock["migrationProductId@Display.placeholder"] = "Not Available";
            jtype.migrationBlock["migrationProductId@Display.readOnly"] = true;

            jtype.migrationBlock.migrationProductInfoURL = "";
            jtype.migrationBlock["migrationProductInfoURL@Display.label"] = "Migration URL";
            jtype.migrationBlock["migrationProductInfoURL@Display.description"] =
                "Cisco Production Info URL";
            jtype.migrationBlock["migrationProductInfoURL@Display.placeholder"] = "Not Available";
            jtype.migrationBlock["migrationProductInfoURL@Display.readOnly"] = true;

            jtype.migrationBlock.migrationStrategy = "";
            jtype.migrationBlock["migrationStrategy@Display.label"] = "Migration Strategy";
            jtype.migrationBlock["migrationStrategy@Display.description"] =
                "Cisco Migration Strategy";
            jtype.migrationBlock["migrationStrategy@Display.placeholder"] = "Not Available";
            jtype.migrationBlock["migrationStrategy@Display.readOnly"] = true;
        } else {
            jtype.migrationBlock.noMigrationData = null;
            jtype.migrationBlock["noMigrationData@Display.label"] =
                "No migration information available from Cisco";
        }

        return jtype;
    }

    /*******************************************/
    /*  END PRIVATE API METHODS                */
    /*******************************************/
}

module.exports = EoxBulletinViewRenderer;
